import {
  QCFinding,
  QCPartialSection,
  QCProgressEvent,
  QCRun,
  QCSectionProgress,
  QCSectionResult,
} from "../types/qcTypes";

/**
 * Read the partial-check detail from a section_error event, if it has one.
 *
 * A long section is checked in parts. When some parts could not be checked,
 * the backend sends section_error with `partial: true` and the part counts;
 * the findings from the checked parts are still sent.
 */
export const partialSectionFromEvent = (
  event: QCProgressEvent,
): QCPartialSection | undefined => {
  if (!event.partial || !event.pieces_total) return undefined;
  const partsTotal = event.pieces_total;
  const partsFailed =
    event.pieces_failed ?? partsTotal - (event.pieces_checked ?? 0);
  return {
    partsTotal,
    partsFailed,
    partsChecked: event.pieces_checked ?? partsTotal - partsFailed,
  };
};

/** A section that was partly checked: some findings are real, some text unchecked. */
export const isPartiallyChecked = (section: QCSectionResult): boolean =>
  section.status === "error" && section.partial !== undefined;

/** A section where nothing could be checked. */
export const isUnchecked = (section: QCSectionResult): boolean =>
  section.status === "error" && section.partial === undefined;

/** "94/95 parts checked" */
export const partialPartsLabel = (partial: QCPartialSection): string =>
  `${partial.partsChecked}/${partial.partsTotal} parts checked`;

/** The sentence shown for a partly checked section. */
export const partialSectionMessage = (partial: QCPartialSection): string =>
  `Partially checked: ${partialPartsLabel(partial)}. Findings from checked parts are shown.`;

/**
 * A section's recorded outcome as section-result fields, or undefined when the
 * server did not record one (older backend, or the section never finished).
 */
export const sectionOutcomeFromProgress = (
  progress: QCSectionProgress | undefined,
): Pick<QCSectionResult, "status" | "errorMessage" | "partial"> | undefined => {
  if (!progress) return undefined;
  if (progress.Status === "completed") return { status: "complete" };
  if (progress.Status !== "failed") return undefined;
  const total = progress.PiecesTotal ?? 0;
  const checked = progress.PiecesChecked ?? 0;
  return {
    status: "error",
    errorMessage: progress.ErrorMessage ?? undefined,
    partial:
      total > 1 && checked > 0 && checked < total
        ? {
            partsTotal: total,
            partsChecked: checked,
            partsFailed: progress.PiecesFailed ?? total - checked,
          }
        : undefined,
  };
};

/**
 * Rebuild section results from a finished run after the stream was lost.
 *
 * The server's findings replace whatever streamed in. Each section takes the
 * server's recorded outcome when there is one, so a partly checked or failed
 * section is not shown as fully checked; without one (older backend) a
 * section that had already failed on the stream keeps that, and the rest are
 * complete, as before.
 */
export const recoverSectionsFromRun = (
  prev: Map<number, QCSectionResult>,
  run: QCRun,
): Map<number, QCSectionResult> => {
  const findingsBySection = new Map<number, QCFinding[]>();
  for (const finding of run.findings) {
    const list = findingsBySection.get(finding.SectionIndex) ?? [];
    list.push(finding);
    findingsBySection.set(finding.SectionIndex, list);
  }
  const progressBySection = new Map<number, QCSectionProgress>(
    (run.sections ?? []).map((row) => [row.SectionIndex, row]),
  );

  const next = new Map(prev);
  const indexes = new Set<number>([
    ...Array.from(prev.keys()),
    ...Array.from(findingsBySection.keys()),
    ...Array.from(progressBySection.keys()),
  ]);
  for (const index of Array.from(indexes)) {
    const existing = prev.get(index);
    const outcome = sectionOutcomeFromProgress(progressBySection.get(index));
    const findings = findingsBySection.get(index) ?? [];
    if (!outcome && existing?.status === "error") continue;
    if (!outcome && !existing && findings.length === 0) continue;
    const progress = progressBySection.get(index);
    next.set(index, {
      sectionIndex: index,
      sectionId: existing?.sectionId ?? progress?.SectionID ?? undefined,
      sectionTitle:
        existing?.sectionTitle ||
        progress?.SectionTitle ||
        findings[0]?.SectionTitle ||
        `Section ${index + 1}`,
      sectionContent: existing?.sectionContent || "",
      findings,
      findingCount: findings.length,
      status: "complete",
      errorMessage: undefined,
      partial: undefined,
      ...outcome,
    });
  }
  return next;
};
